import type { FunctionalComponent } from 'preact';
import { useEffect, useRef, useState, useMemo } from 'preact/hooks';
import { useStore } from '../store';
import {
    createWidgetPurchase,
    completeSale,
    calculateFees,
    createFreeTicket,
    verifyPaystackPurchase,
    verifyFlutterwavePurchase,
    type CompleteSaleExtras,
    type CreateFreeTicketOptions,
} from '../api/client';
import {
    extractFlutterwaveReturnFromUrl,
    extractPaystackReferenceFromUrl,
    resolveChargeCurrency,
    type PaystackWidgetSessionContext,
} from '../lib/paymentRail';
import { formatCurrency, formatCurrencyMajor } from '../utils/theme';
import { sendError } from '../utils/postMessage';
import { InlineSponsorPlayer } from './SponsorVideo';
import { PromoCode } from './PromoCode';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { extractShouldRegister } from '../lib/extractShouldRegister';
import {
    canUseFeePreviewForFullOrder,
    sumTicketLinesCents,
    sumSeatLinesCentsPreferPreview,
    sumTableLinesCentsPreferPreview,
    sumOrderDiscountableAndFixedCents,
    orderTotalAfterCartPromoCents,
    getDisplayPrice,
    getTicketLineSubtotalCents,
} from '../lib/tierCartPricing';
import { formatFeePreviewInclusions, lineTotalMajorForTableRule } from '../lib/feePreview';
import { slotLabel } from '../lib/tableMapUtils';
import { firstWidgetFeeTierForTableFlow } from '../lib/feeTierDesignation';

export const Payment: FunctionalComponent = () => {
    const { state, dispatch } = useStore();
    const elementsRef = useRef<StripeElements | null>(null);
    const stripeRef = useRef<Stripe | null>(null);
    const mountRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
    const [saleId, setSaleId] = useState<string | null>(null);
    const [stripeRegion, setStripeRegion] = useState<string | null>(null);
    const [hostedProvider, setHostedProvider] = useState<'paystack' | 'flutterwave' | null>(null);
    const [isPaystackCheckout, setIsPaystackCheckout] = useState(false);
    const [paystackAuthUrl, setPaystackAuthUrl] = useState<string | null>(null);
    const [paystackReference, setPaystackReference] = useState<string | null>(null);

    /** Sponsor view/lead metadata — read from ref during Stripe init so video steps don't remount Elements. */
    const sponsorCheckoutMetaRef = useRef({
        viewCorrelationId: state.viewCorrelationId,
        watchedSponsorAttributions: state.watchedSponsorAttributions,
        sponsorLeadConsent: state.sponsorLeadConsent,
    });
    useEffect(() => {
        sponsorCheckoutMetaRef.current = {
            viewCorrelationId: state.viewCorrelationId,
            watchedSponsorAttributions: state.watchedSponsorAttributions,
            sponsorLeadConsent: state.sponsorLeadConsent,
        };
    }, [state.viewCorrelationId, state.watchedSponsorAttributions, state.sponsorLeadConsent]);

    const tableFallbackTier = useMemo(
        () => firstWidgetFeeTierForTableFlow(state.feeStructures, !!state.event?.seated_event),
        [state.feeStructures, state.event?.seated_event],
    );

    const currency = useMemo(() => {
        if (state.cart.length > 0) return state.cart[0].tier.currency;
        if (state.selectedTables.length > 0) {
            return tableFallbackTier?.currency || state.feeStructures[0]?.currency || 'USD';
        }
        return state.feeStructures[0]?.currency || 'USD';
    }, [state.cart, state.selectedTables.length, tableFallbackTier, state.feeStructures]);

    const chargeCurrency = useMemo(
        () => resolveChargeCurrency(currency, state.paymentRail),
        [currency, state.paymentRail],
    );

    const feeTierFallbackForTables = tableFallbackTier?._id ?? state.feeStructures[0]?._id;

    const feePreviewOnlyCheckout = useMemo(
        () =>
            canUseFeePreviewForFullOrder(
                state.cart,
                state.unlockedSponsorTiers,
                state.selectedSeats,
                state.selectedTables,
                state.tableReservationHoldCents,
            ),
        [
            state.cart,
            state.unlockedSponsorTiers,
            state.selectedSeats,
            state.selectedTables,
            state.tableReservationHoldCents,
        ],
    );

    /** Cents. Prefer `fee_preview.total_with_fees` per line so promos apply to all-in, not list + sales-calc. */
    const ticketListCents = useMemo(
        () => (state.cart.length === 0 ? 0 : sumTicketLinesCents(state.cart, state.unlockedSponsorTiers)),
        [state.cart, state.unlockedSponsorTiers],
    );

    const seatLineCents = useMemo(() => sumSeatLinesCentsPreferPreview(state.selectedSeats), [state.selectedSeats]);

    const tableLineCents = useMemo(
        () => sumTableLinesCentsPreferPreview(state.selectedTables, state.tableReservationHoldCents),
        [state.selectedTables, state.tableReservationHoldCents],
    );

    const prePromoCents = useMemo(
        () => ticketListCents + seatLineCents + tableLineCents,
        [ticketListCents, seatLineCents, tableLineCents],
    );

    const orderSplitCents = useMemo(
        () =>
            sumOrderDiscountableAndFixedCents(
                state.cart,
                state.unlockedSponsorTiers,
                state.selectedSeats,
                state.selectedTables,
                state.tableReservationHoldCents,
            ),
        [
            state.cart,
            state.unlockedSponsorTiers,
            state.selectedSeats,
            state.selectedTables,
            state.tableReservationHoldCents,
        ],
    );

    const afterPromoCents = useMemo(
        () => orderTotalAfterCartPromoCents(prePromoCents, orderSplitCents, state.discount),
        [prePromoCents, orderSplitCents, state.discount],
    );

    /** After promo, in **major** units (for `formatCurrencyMajor` and legacy paths). */
    const cartSubtotal = useMemo(() => afterPromoCents / 100, [afterPromoCents]);

    const fees = useMemo(() => {
        if (feePreviewOnlyCheckout || state.discount) return 0;
        if (!state.feeCalculation) return 0;
        if (state.feeCalculation.totals?.total_fees !== undefined) {
            return state.feeCalculation.totals.total_fees;
        }
        if (state.feeCalculation.breakdown?.total_fees !== undefined) {
            return state.feeCalculation.breakdown.total_fees;
        }
        if (state.feeCalculation.total_fees !== undefined) {
            return state.feeCalculation.total_fees;
        }
        return 0;
    }, [state.feeCalculation, feePreviewOnlyCheckout, state.discount]);

    const tax = useMemo(() => {
        if (feePreviewOnlyCheckout || state.discount) return 0;
        if (!state.feeCalculation) return 0;
        const t = state.feeCalculation.totals?.tax ?? state.feeCalculation.breakdown?.tax;
        return t ?? 0;
    }, [state.feeCalculation, feePreviewOnlyCheckout, state.discount]);

    /** All cent amounts from `sales-calc` (subtotal in UI is `cartSubtotal` major). */
    const grandTotal = useMemo(() => {
        if (feePreviewOnlyCheckout || state.discount) return afterPromoCents;
        const apiTotal = state.feeCalculation?.totals?.total_amount ?? state.feeCalculation?.breakdown?.total_amount;
        if (apiTotal !== undefined) return apiTotal;
        return Math.max(0, afterPromoCents) + fees + tax;
    }, [feePreviewOnlyCheckout, state.discount, afterPromoCents, state.feeCalculation, fees, tax]);

    const allFree = afterPromoCents <= 0;

    useEffect(() => {
        if (allFree) {
            dispatch({ type: 'CLEAR_FEE_CALCULATION' });
            return;
        }
        if (feePreviewOnlyCheckout || state.discount) {
            dispatch({ type: 'CLEAR_FEE_CALCULATION' });
            dispatch({ type: 'SET_CALCULATING_FEES', calculating: false });
            return;
        }

        let cancelled = false;
        dispatch({ type: 'SET_CALCULATING_FEES', calculating: true });

        const eventId = state.event?._id;
        calculateFees(Math.max(0, afterPromoCents), chargeCurrency, 'tickets', undefined, eventId)
            .then(data => {
                if (!cancelled) {
                    dispatch({ type: 'SET_FEE_CALCULATION', feeCalculation: data });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    dispatch({ type: 'CLEAR_FEE_CALCULATION' });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [afterPromoCents, chargeCurrency, allFree, state.event?._id, feePreviewOnlyCheckout, state.discount, dispatch]);

    useEffect(() => {
        if (typeof window === 'undefined' || allFree) return;
        const flw = extractFlutterwaveReturnFromUrl(window.location.href);
        const paystackReference = extractPaystackReferenceFromUrl(window.location.href);
        if (!flw && !paystackReference) return;

        const ctxRaw = sessionStorage.getItem('meeteor_paystack_widget_context');
        if (!ctxRaw) return;

        let cancelled = false;
        (async () => {
            setProcessing(true);
            setError(null);
            try {
                let ctxProvider: 'paystack' | 'flutterwave' | undefined;
                try {
                    const parsed = JSON.parse(ctxRaw) as PaystackWidgetSessionContext;
                    ctxProvider = parsed.provider;
                } catch {
                    /* ignore */
                }

                const isFlutterwave =
                    ctxProvider === 'flutterwave' ||
                    Boolean(flw?.transaction_id) ||
                    (Boolean(flw?.tx_ref) && ctxProvider !== 'paystack');

                const verified = isFlutterwave
                    ? await verifyFlutterwavePurchase({
                          transaction_id: flw?.transaction_id,
                          tx_ref: flw?.tx_ref || flw?.reference || paystackReference || undefined,
                          reference: flw?.tx_ref || flw?.reference || paystackReference || undefined,
                      })
                    : await verifyPaystackPurchase(paystackReference!);

                if (cancelled) return;
                if (verified.paid === false) {
                    setError(
                        `${isFlutterwave ? 'Flutterwave' : 'Paystack'} payment was not completed.`,
                    );
                    setProcessing(false);
                    return;
                }

                // Hosted checkout is finalized by verify — do not POST /sales with the reference
                sessionStorage.removeItem('meeteor_paystack_widget_context');

                const verifiedRef =
                    verified.reference ||
                    verified.tx_ref ||
                    paystackReference ||
                    flw?.tx_ref ||
                    '';

                dispatch({
                    type: 'SET_PURCHASE_RESULT',
                    result: {
                        saleId: verified.saleId || sessionStorage.getItem('meeteor_paystack_sale_id') || '',
                        paymentIntentId: verifiedRef,
                        shouldRegister: false,
                    },
                });
                sessionStorage.removeItem('meeteor_paystack_sale_id');
                const cleanUrl = window.location.pathname + window.location.hash;
                window.history.replaceState({}, '', cleanUrl);
                setProcessing(false);
            } catch (e: any) {
                if (!cancelled) {
                    setError(e.message || 'Failed to verify payment');
                    setProcessing(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [allFree, dispatch]);

    useEffect(() => {
        if (allFree) {
            setLoading(false);
            return;
        }
        if (
            typeof window !== 'undefined' &&
            (extractPaystackReferenceFromUrl(window.location.href) ||
                extractFlutterwaveReturnFromUrl(window.location.href))
        ) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        async function init() {
            try {
                if (mountRef.current) {
                    mountRef.current.innerHTML = '';
                }
                elementsRef.current = null;

                const feeBreakdown = feePreviewOnlyCheckout
                    ? undefined
                    : (state.feeCalculation?.totals ?? state.feeCalculation?.breakdown ?? undefined);
                const feeTotal = feePreviewOnlyCheckout
                    ? afterPromoCents
                    : (state.feeCalculation?.totals?.total_amount ?? state.feeCalculation?.breakdown?.total_amount);

                const feeTierFallback = feeTierFallbackForTables;
                const selectedTablePayload =
                    state.selectedTables.length > 0
                        ? state.selectedTables.map(t => ({
                              table_id: t.tableId,
                              chairs: t.chairs,
                              label: t.label,
                              time_slot: t.timeSlot || null,
                              price: lineTotalMajorForTableRule(t.pricingRule),
                              fee_tier_id: t.pricingRule?.fee_tier_id || feeTierFallback,
                              free_tickets_count: t.pricingRule?.free_tickets_count ?? 0,
                          }))
                        : null;

                const sponsorMeta = sponsorCheckoutMetaRef.current;
                const res = await createWidgetPurchase(
                    state.widgetId,
                    state.cart,
                    state.contact,
                    state.event!._id,
                    currency,
                    1,
                    state.discount || undefined,
                    feeBreakdown,
                    feeTotal,
                    state.holdId || null,
                    selectedTablePayload,
                    Object.keys(state.unlockedSponsorTiers).length > 0 ? state.unlockedSponsorTiers : undefined,
                    state.refCode?.trim() || undefined,
                    sponsorMeta.viewCorrelationId,
                    sponsorMeta.watchedSponsorAttributions.length > 0
                        ? sponsorMeta.watchedSponsorAttributions
                        : undefined,
                    state.selectedOccurrenceIso,
                    state.paymentRail,
                    sponsorMeta.sponsorLeadConsent,
                );

                if (cancelled) return;

                const purchaseData = res.data;
                const provider =
                    purchaseData.provider ??
                    (purchaseData.authorization_url
                        ? purchaseData.payment_rail?.use_flutterwave
                            ? 'flutterwave'
                            : 'paystack'
                        : 'stripe');
                setSaleId(purchaseData.saleId || null);
                setStripeRegion(
                    purchaseData.stripe_region ||
                        purchaseData.payment_rail?.stripe_region ||
                        state.paymentRail?.stripe_region ||
                        null,
                );

                if (
                    (provider === 'paystack' || provider === 'flutterwave') &&
                    purchaseData.authorization_url
                ) {
                    setHostedProvider(provider);
                    setIsPaystackCheckout(true);
                    setPaystackAuthUrl(purchaseData.authorization_url);
                    setPaystackReference(purchaseData.reference || purchaseData.tx_ref || null);
                    if (purchaseData.saleId) {
                        sessionStorage.setItem('meeteor_paystack_sale_id', purchaseData.saleId);
                    }
                    sessionStorage.setItem(
                        'meeteor_paystack_widget_context',
                        JSON.stringify({
                            widgetId: state.widgetId,
                            eventId: state.event!._id,
                            chargeCurrency,
                            provider,
                            contact: state.contact,
                            feeBreakdown: feePreviewOnlyCheckout
                                ? undefined
                                : (state.feeCalculation?.totals ?? state.feeCalculation?.breakdown ?? undefined),
                            feeTotal: feePreviewOnlyCheckout
                                ? afterPromoCents
                                : (state.feeCalculation?.totals?.total_amount ??
                                  state.feeCalculation?.breakdown?.total_amount),
                            refCode: state.refCode?.trim() || undefined,
                            viewCorrelationId: sponsorMeta.viewCorrelationId,
                            sponsorAttributions:
                                sponsorMeta.watchedSponsorAttributions.length > 0
                                    ? sponsorMeta.watchedSponsorAttributions
                                    : undefined,
                            sponsorLeadConsent: sponsorMeta.sponsorLeadConsent,
                            selectedOccurrenceIso: state.selectedOccurrenceIso,
                            cartTierIds: state.cart.map(c => c.tier._id),
                            cartQuantities: state.cart.map(c => c.quantity),
                            cartSnapshot: state.cart.map(c => ({
                                tierId: c.tier._id,
                                quantity: c.quantity,
                                tier: {
                                    _id: c.tier._id,
                                    fee_name: c.tier.fee_name,
                                    fee: c.tier.fee,
                                    currency: c.tier.currency,
                                    description: c.tier.description,
                                    sponsor: c.tier.sponsor,
                                    sponsor_discount: c.tier.sponsor_discount,
                                    sponsur_free_tier: c.tier.sponsur_free_tier,
                                },
                            })),
                            selectedTables: state.selectedTables,
                            selectedSeats: state.selectedSeats,
                            holdId: state.holdId,
                            unlockedSponsorTiers: state.unlockedSponsorTiers,
                            discount: state.discount,
                        }),
                    );
                    setLoading(false);
                    return;
                }

                const stripeKey =
                    purchaseData.stripe_publishable_key || state.stripePublishableKey;
                const stripe = await loadStripe(stripeKey);
                if (!stripe || cancelled) return;
                stripeRef.current = stripe;

                if (!purchaseData.clientSecret) {
                    throw new Error('Missing Stripe client secret');
                }
                setClientSecret(purchaseData.clientSecret);
                setPaymentIntentId(purchaseData.paymentIntentId || '');

                const theme = state.widget?.theme;
                const fontFamily = theme?.font_family || 'Inter';
                const fontStack = `"${fontFamily}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
                const fontCssSrc = theme?.font_url
                    ? theme.font_url
                    : `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}:wght@400;500;600;700&display=swap`;

                const elements = stripe.elements({
                    clientSecret: purchaseData.clientSecret,
                    fonts: [{ cssSrc: fontCssSrc }],
                    appearance: {
                        theme: 'stripe',
                        variables: {
                            colorPrimary: theme?.primary_color || '#6C63FF',
                            colorBackground: theme?.background_color || '#ffffff',
                            colorText: theme?.text_color || '#1A1A2E',
                            colorTextPlaceholder: '#9ca3af',
                            borderRadius: `${theme?.border_radius ?? 12}px`,
                            fontFamily: fontStack,
                            fontSizeBase: '16px',
                            fontSmooth: 'always',
                        },
                    },
                });

                elementsRef.current = elements;
                const paymentElement = elements.create('payment');
                if (mountRef.current) {
                    paymentElement.mount(mountRef.current);
                }
                setLoading(false);
            } catch (e: any) {
                if (!cancelled) {
                    setError(e.message || 'Failed to initialize payment');
                    setLoading(false);
                }
            }
        }

        init();
        return () => {
            cancelled = true;
            if (mountRef.current) {
                mountRef.current.innerHTML = '';
            }
        };
    }, [
        allFree,
        feePreviewOnlyCheckout,
        afterPromoCents,
        state.feeCalculation,
        state.cart,
        state.contact,
        state.widgetId,
        state.event?._id,
        currency,
        state.stripePublishableKey,
        state.holdId,
        state.tableReservationHoldCents,
        state.unlockedSponsorTiers,
        state.refCode,
        state.selectedTables,
        state.discount,
        state.selectedOccurrenceIso,
        state.paymentRail,
        chargeCurrency,
    ]);

    async function handleFreeClaim() {
        if (!state.event) return;
        setProcessing(true);
        setError(null);
        try {
            const feeTierFallback = feeTierFallbackForTables;
            const freeOpts: CreateFreeTicketOptions = {
                correlationId: state.viewCorrelationId,
                refCode: state.refCode?.trim() || undefined,
                viewCorrelationId: state.viewCorrelationId,
                sponsorAttributions:
                    state.watchedSponsorAttributions.length > 0 ? state.watchedSponsorAttributions : undefined,
                selectedOccurrenceIso: state.selectedOccurrenceIso,
                sponsorLeadConsent: state.sponsorLeadConsent,
            };
            if (state.selectedTables.length > 0) {
                freeOpts.tableHoldId = state.holdId;
                freeOpts.selectedTables = state.selectedTables.map(t => ({
                    table_id: t.tableId,
                    chairs: t.chairs,
                    label: t.label,
                    time_slot: t.timeSlot || null,
                    price: lineTotalMajorForTableRule(t.pricingRule),
                    fee_tier_id: t.pricingRule?.fee_tier_id || feeTierFallback,
                    free_tickets_count: t.pricingRule?.free_tickets_count ?? 0,
                }));
            } else if (state.selectedSeats.length > 0) {
                freeOpts.seatHoldId = state.holdId;
                freeOpts.selectedSeats = state.selectedSeats;
            }

            const res = await createFreeTicket(state.cart, state.contact, state.event._id, freeOpts);
            const r = res as {
                data?: { saleId?: string; email_upsell?: { message: string; url: string } };
                shouldRegister?: boolean;
            };
            const emailUpsell = r?.data?.email_upsell;
            const shouldRegister = extractShouldRegister(r) !== false;
            dispatch({
                type: 'SET_PURCHASE_RESULT',
                result: {
                    saleId: r?.data?.saleId || '',
                    paymentIntentId: '',
                    ...(emailUpsell && { email_upsell: emailUpsell }),
                    shouldRegister,
                },
            });
        } catch (e: any) {
            setError(e.message || 'Failed to reserve free tickets');
            sendError('FREE_TICKET_FAILED', e.message || 'Failed to reserve free tickets');
        } finally {
            setProcessing(false);
        }
    }

    async function handlePay() {
        if (isPaystackCheckout && paystackAuthUrl) {
            setProcessing(true);
            setError(null);
            window.location.href = paystackAuthUrl;
            return;
        }

        const stripe = stripeRef.current;
        const elements = elementsRef.current;
        if (!stripe || !elements || !clientSecret) return;

        setProcessing(true);
        setError(null);

        try {
            const { error: stripeError } = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: window.location.href },
                redirect: 'if_required',
            });

            if (stripeError) {
                setError(stripeError.message || 'Payment failed');
                sendError('PAYMENT_FAILED', stripeError.message || 'Payment failed');
                setProcessing(false);
                return;
            }

            let emailUpsell: { message: string; url: string } | undefined;
            let shouldRegister = false;
            if (paymentIntentId && state.contact.email) {
                const feeBreakdown = feePreviewOnlyCheckout
                    ? undefined
                    : (state.feeCalculation?.totals ?? state.feeCalculation?.breakdown ?? undefined);
                const feeTotal = feePreviewOnlyCheckout
                    ? afterPromoCents
                    : (state.feeCalculation?.totals?.total_amount ?? state.feeCalculation?.breakdown?.total_amount);

                let completeExtras: CompleteSaleExtras | undefined;
                if (state.selectedTables.length > 0) {
                    const ft = feeTierFallbackForTables;
                    completeExtras = {
                        tableHoldId: state.holdId,
                        selectedTables: state.selectedTables.map(t => ({
                            table_id: t.tableId,
                            chairs: t.chairs,
                            label: t.label,
                            time_slot: t.timeSlot || null,
                            price: lineTotalMajorForTableRule(t.pricingRule),
                            fee_tier_id: t.pricingRule?.fee_tier_id || ft,
                            free_tickets_count: t.pricingRule?.free_tickets_count ?? 0,
                        })),
                    };
                } else if (state.selectedSeats.length > 0) {
                    completeExtras = {
                        seatHoldId: state.holdId,
                        selectedSeats: state.selectedSeats,
                    };
                }

                try {
                    const saleRes = await completeSale(
                        paymentIntentId,
                        state.contact.email,
                        saleId || '',
                        state.cart,
                        state.event!._id,
                        chargeCurrency,
                        state.contact,
                        feeBreakdown,
                        feeTotal,
                        state.refCode?.trim() || undefined,
                        state.viewCorrelationId,
                        state.watchedSponsorAttributions.length > 0 ? state.watchedSponsorAttributions : undefined,
                        completeExtras,
                        state.selectedOccurrenceIso,
                        Object.keys(state.unlockedSponsorTiers).length > 0 ? state.unlockedSponsorTiers : undefined,
                        'stripe',
                        stripeRegion || state.paymentRail?.stripe_region || null,
                        state.sponsorLeadConsent,
                    );
                    const r = saleRes as {
                        data?: { email_upsell?: { message: string; url: string } };
                        shouldRegister?: boolean;
                    };
                    emailUpsell = r?.data?.email_upsell;
                    shouldRegister = extractShouldRegister(saleRes) !== false;
                } catch (e: unknown) {
                    const msg =
                        (e as { message?: string })?.message ||
                        'Order could not be completed. If you were charged, contact support with your email and event.';
                    setError(msg);
                    sendError('COMPLETE_SALE_FAILED', msg);
                    setProcessing(false);
                    return;
                }
            }

            dispatch({
                type: 'SET_PURCHASE_RESULT',
                result: {
                    saleId: saleId || '',
                    paymentIntentId: paymentIntentId || '',
                    ...(emailUpsell && { email_upsell: emailUpsell }),
                    shouldRegister,
                },
            });
            setProcessing(false);
        } catch (e: any) {
            setError(e.message || 'Payment failed');
            sendError('PAYMENT_FAILED', e.message || 'Payment failed');
            setProcessing(false);
        }
    }

    return (
        <div style={{ padding: '1rem' }}>
            <div class="mw-section-title" style={{ padding: 0, marginBottom: '0.5rem' }}>
                Order Summary
            </div>

            {/* Ticket line items */}
            {state.cart.map(c => {
                const sponsorUnlock = state.unlockedSponsorTiers[c.tier._id];
                const { effective } = getDisplayPrice(c.tier, !!sponsorUnlock);
                const fp = c.tier.fee_preview;
                const lineCents = getTicketLineSubtotalCents(c, state.unlockedSponsorTiers);
                const useAllInLabel = !!fp?.formatted_total && fp?.total_with_fees != null && effective === c.tier.fee;
                return (
                    <div key={c.tier._id} class="mw-cart-line">
                        <span>
                            {c.quantity}× {c.tier.fee_name}
                        </span>
                        <span>
                            {useAllInLabel && c.quantity === 1
                                ? fp.formatted_total
                                : formatCurrency(lineCents, currency)}
                        </span>
                    </div>
                );
            })}

            {state.selectedSeats.length > 0 && (
                <div class="mw-cart-line">
                    <span>
                        {state.selectedSeats.length} seat{state.selectedSeats.length !== 1 ? 's' : ''}
                    </span>
                    <span>{formatCurrency(seatLineCents, currency)}</span>
                </div>
            )}

            {state.selectedTables.length > 0 &&
                (state.tableReservationHoldCents != null ? (
                    <div class="mw-cart-line" style={{ alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                            <span>
                                Table{state.selectedTables.length !== 1 ? 's' : ''} ({state.selectedTables.length})
                            </span>
                            {(() => {
                                const parts = state.selectedTables
                                    .map(t => formatFeePreviewInclusions(t.pricingRule?.fee_preview))
                                    .filter(Boolean);
                                const uniq = [...new Set(parts)];
                                if (uniq.length === 0) return null;
                                return (
                                    <span
                                        style={{
                                            fontSize: '0.75rem',
                                            color: 'var(--mw-text-muted)',
                                            marginTop: '0.2rem',
                                        }}>
                                        {uniq.join(' · ')}
                                    </span>
                                );
                            })()}
                        </div>
                        <span>{formatCurrency(tableLineCents, currency)}</span>
                    </div>
                ) : (
                    state.selectedTables.map(t => {
                        const tCents = sumTableLinesCentsPreferPreview([t], null);
                        const inc = formatFeePreviewInclusions(t.pricingRule?.fee_preview);
                        const sub = t.timeSlot
                            ? slotLabel({
                                  day: t.timeSlot.day,
                                  start_time: t.timeSlot.start_time,
                                  end_time: t.timeSlot.end_time,
                              })
                            : '';
                        return (
                            <div key={`${t.tableId}-${sub}`} class="mw-cart-line" style={{ alignItems: 'flex-start' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                    <span>
                                        {t.label}
                                        {sub ? ` · ${sub}` : ''}
                                    </span>
                                    {inc ? (
                                        <span
                                            style={{
                                                fontSize: '0.75rem',
                                                color: 'var(--mw-text-muted)',
                                                marginTop: '0.2rem',
                                            }}>
                                            {inc}
                                        </span>
                                    ) : null}
                                </div>
                                <span>{formatCurrency(tCents, currency)}</span>
                            </div>
                        );
                    })
                ))}

            {state.discount && (
                <div class="mw-cart-line">
                    <span style={{ color: 'var(--mw-success)' }}>
                        Discount (
                        {state.discount.type === 'percentage'
                            ? `${state.discount.value}%`
                            : formatCurrency(state.discount.value, currency)}
                        )
                    </span>
                    <span style={{ color: 'var(--mw-success)' }}>
                        −{formatCurrency(prePromoCents - afterPromoCents, currency)}
                    </span>
                </div>
            )}

            <div
                class="mw-cart-line"
                style={{ borderTop: '1px solid var(--mw-border)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
                <span>Subtotal</span>
                <span>{formatCurrencyMajor(cartSubtotal, currency)}</span>
            </div>

            {feePreviewOnlyCheckout && !allFree && (
                <p style={{ fontSize: '0.75rem', color: 'var(--mw-text-muted)', margin: '0.25rem 0 0' }}>
                    Subtotal and total include all fees and tax from the server (fee_preview; no local fee or tax math)
                </p>
            )}

            {!allFree && !feePreviewOnlyCheckout && (
                <div class="mw-cart-line">
                    <span style={{ color: 'var(--mw-text-muted)', fontSize: '0.85rem' }}>Service Fees</span>
                    <span style={{ color: 'var(--mw-text-muted)', fontSize: '0.85rem' }}>
                        {state.calculatingFees ? '...' : formatCurrency(fees, currency)}
                    </span>
                </div>
            )}

            {!allFree && !feePreviewOnlyCheckout && tax > 0 && (
                <div class="mw-cart-line">
                    <span style={{ color: 'var(--mw-text-muted)', fontSize: '0.85rem' }}>Tax</span>
                    <span style={{ color: 'var(--mw-text-muted)', fontSize: '0.85rem' }}>
                        {state.calculatingFees ? '...' : formatCurrency(tax, currency)}
                    </span>
                </div>
            )}

            <div
                class="mw-cart-line"
                style={{
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    borderTop: '1px solid var(--mw-border)',
                    paddingTop: '0.5rem',
                    marginTop: '0.25rem',
                    marginBottom: '1rem',
                }}>
                <span>Total</span>
                <span>
                    {state.calculatingFees && !feePreviewOnlyCheckout ? '...' : formatCurrency(grandTotal, currency)}
                </span>
            </div>

            {/* Inline sponsor video players for paid tiers with discount in cart */}
            {state.cart
                .filter(
                    c =>
                        c.tier.sponsor &&
                        c.tier.sponsor_discount &&
                        !c.tier.sponsur_free_tier &&
                        !(c.tier.sponsor_discount.discount >= 100),
                )
                .map(c => (
                    <div key={`sponsor-${c.tier._id}`} style={{ marginBottom: '0.75rem' }}>
                        <InlineSponsorPlayer tier={c.tier} compact />
                    </div>
                ))}

            {error && (
                <div class="mw-field-error" style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                    {error}
                </div>
            )}

            {allFree ? (
                <button
                    type="button"
                    class="mw-btn mw-btn--primary mw-btn--block"
                    onClick={handleFreeClaim}
                    disabled={processing}
                    style={{ marginTop: '1rem' }}>
                    {processing ? 'Reserving...' : 'Confirm Free Tickets'}
                </button>
            ) : (
                <>
                    {state.selectedTables.length > 0 &&
                        state.widget?.enable_promo_codes &&
                        state.discount?.source !== 'sponsor' && (
                            <div class="mw-payment-promo" style={{ marginBottom: '1.25rem' }}>
                                <PromoCode />
                            </div>
                        )}

                    {loading && (
                        <div class="mw-loading">
                            <div class="mw-spinner" />
                            <span style={{ fontSize: '0.85rem', color: 'var(--mw-text-muted)' }}>
                                Loading payment form...
                            </span>
                        </div>
                    )}

                    <div
                        ref={mountRef}
                        class="mw-stripe-container"
                        style={{ display: loading || isPaystackCheckout ? 'none' : 'block', padding: 0 }}
                    />

                    {isPaystackCheckout && !loading && (
                        <p style={{ fontSize: '0.85rem', color: 'var(--mw-text-muted)', marginTop: '0.5rem' }}>
                            You will complete payment securely with{' '}
                            {hostedProvider === 'flutterwave' ? 'Flutterwave' : 'Paystack'}.
                        </p>
                    )}

                    {!loading && (
                        <button
                            type="button"
                            class="mw-btn mw-btn--primary mw-btn--block"
                            onClick={handlePay}
                            disabled={processing || (state.calculatingFees && !feePreviewOnlyCheckout)}
                            style={{ marginTop: '1rem' }}>
                            {processing
                                ? 'Processing...'
                                : isPaystackCheckout
                                  ? `Continue to ${
                                        hostedProvider === 'flutterwave' ? 'Flutterwave' : 'Paystack'
                                    } — ${formatCurrency(grandTotal, currency)}`
                                  : `Pay ${formatCurrency(grandTotal, currency)}`}
                        </button>
                    )}
                </>
            )}
        </div>
    );
};
